/**
 * ReadyJobBookingDialog — book an install from an empty calendar slot.
 *
 * Only surfaces jobs the readiness engine considers bookable:
 *   • "ready"    — no blockers (default, recommended)
 *   • "warning"  — minor gaps (opt-in, shown with a warning badge)
 *
 * Jobs that are both scheduled and assigned are filtered out so we never
 * double-book the same project. Scheduled jobs with no crew/sub assignment
 * stay visible because this modal is the recovery path for assigning them.
 *
 * On confirm, creates a real ScheduledEvent via useScheduling().addEvent —
 * which auto-emits INSTALL_SCHEDULED through the audit/event bus.
 */
import { useEffect, useMemo, useState } from "react";
import { parseISO } from "date-fns";
import { CalendarClock, ShieldCheck, ShieldAlert, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAppData } from "@/data/AppDataContext";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import { getOnboardingLifecycleRows } from "@/data/contractor/onboarding/lifecycle";
import { useScheduling } from "@/data/scheduling/context";
import { getCanonicalInstallReadiness } from "@/data/readiness";
import { derivePreSpendGate } from "@/data/preSpendGate";
import { getDispatchRecommendations } from "@/data/dispatchRecommendation";
import { getKit } from "@/data/materialKit";
import { EVENT_TYPE_DEPARTMENT } from "@/data/scheduling/types";
import { toast } from "@/hooks/use-toast";
import { JobService } from "@/services/job.service";
import { JobOperationsService } from "@/services/job-operations.service";
import { getJobBriefing } from "@/data/contractor/jobBriefing";
import { getPortalSpec } from "@/data/portal/contractorPortalSelectors";
import { getJobPhotoUrls } from "@/services/attachment.service";
import {
  advanceInstallDay,
  createInstallDateTime,
  formatInstallDateIso,
  formatInstallDateTimeLabel,
  getInstallEndForShifts,
  INSTALL_TIME_ZONE,
} from "@/data/scheduling/installSpan";
import type { Job } from "@/data/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Slot start (date + hour). Booking will use this as the start time. */
  slotStart: Date | null;
  /** Allows quick-pick suggestions to move the selected booking slot. */
  onSlotStartChange?: (slotStart: Date) => void;
  /** Default duration in hours; user can adjust. */
  defaultDurationHours?: number;
  /** When provided, this job is pre-selected and pinned to the top of the list. */
  preselectJobId?: string | null;
  /** Optional precomputed "ready to book" gate from Plan rail to keep modal in exact sync. */
  scheduleReadyByJobId?: Map<string, { scheduleReady: boolean; blocker: string | null }>;
}

type BookingDurationOption = {
  value: string;
  label: string;
  hours?: number;
  days?: number;
};

const DURATION_OPTIONS: BookingDurationOption[] = [
  { value: "2h", label: "2 hours", hours: 2 },
  { value: "4h", label: "4 hours", hours: 4 },
  { value: "6h", label: "6 hours", hours: 6 },
  { value: "8h", label: "1 day", hours: 8 },
  { value: "2d", label: "2 days", days: 2 },
  { value: "3d", label: "3 days", days: 3 },
  { value: "4d", label: "4 days", days: 4 },
  { value: "5d", label: "5 days", days: 5 },
];

const getDefaultDurationValue = (hours: number) => {
  if (hours <= 2) return "2h";
  if (hours <= 4) return "4h";
  if (hours <= 6) return "6h";
  return "8h";
};

const getDurationOption = (value: string) =>
  DURATION_OPTIONS.find((option) => option.value === value) ?? DURATION_OPTIONS[1];

const getBookingEnd = (start: Date, duration: BookingDurationOption) => {
  if (duration.days && duration.days > 1) {
    return getInstallEndForShifts(start, duration.days);
  }

  const hours = duration.hours ?? 4;
  return new Date(start.getTime() + hours * 60 * 60 * 1000);
};

const getBookingShiftCount = (duration: BookingDurationOption) => duration.days ?? 1;

const BOOKING_IGNORE_LABELS = new Set(["Schedule", "Crew Assignment"]);
type CrewConflictInfo = {
  crewId: string;
  title: string;
  startTime: string;
  endTime: string;
};

type InstallDateSuggestion = {
  id: string;
  crewId: string;
  crewName: string;
  start: Date;
  end: Date;
};

const getJobDisplayRef = (job: { id?: string | null; jobNumber?: string | null }) => {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  if (jobNumber.length > 0) return jobNumber;
  return String(job.id ?? "");
};

function isWeekendDateIso(dateIso: string): boolean {
  const [year, month, day] = dateIso.split("-").map(Number);
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  const dow = utcDate.getUTCDay();
  return dow === 0 || dow === 6;
}

function getNextInstallBusinessDateIso(dateIso: string): string {
  let cursorIso = dateIso;
  while (isWeekendDateIso(cursorIso)) {
    cursorIso = formatInstallDateIso(advanceInstallDay(createInstallDateTime(cursorIso, 9)));
  }
  return cursorIso;
}

function overlapsEvent(
  start: Date,
  end: Date,
  crewId: string,
  events: Array<{ crewId: string; status: string; startTime: string; endTime: string }>,
): boolean {
  const startMs = start.getTime();
  const endMs = end.getTime();
  return events.some((event) => {
    if (event.crewId !== crewId) return false;
    if (event.status === "cancelled" || event.status === "completed") return false;
    const eventStart = parseISO(event.startTime).getTime();
    const eventEnd = parseISO(event.endTime).getTime();
    return startMs < eventEnd && eventStart < endMs;
  });
}

function formatSuggestionRange(suggestion: InstallDateSuggestion, duration: BookingDurationOption): string {
  if (!duration.days || duration.days <= 1) {
    return formatInstallDateTimeLabel(suggestion.start);
  }

  const startDay = formatInstallDateIso(suggestion.start);
  const endDay = formatInstallDateIso(suggestion.end);
  if (startDay === endDay) {
    return formatInstallDateTimeLabel(suggestion.start);
  }

  return `${formatInstallDateTimeLabel(suggestion.start)} to ${formatInstallDateTimeLabel(suggestion.end)}`;
}

function hasInstallerAssignment(job: Pick<Job, "assignedCrew" | "assignedSubcontractor" | "assignedSubcontractorId" | "crewId" | "assignmentType">): boolean {
  const hasAssignee =
    Boolean(String(job.assignedCrew ?? "").trim()) ||
    Boolean(String(job.assignedSubcontractor ?? "").trim()) ||
    Boolean(String(job.assignedSubcontractorId ?? "").trim()) ||
    Boolean(String(job.crewId ?? "").trim());

  return hasAssignee && job.assignmentType !== "Unassigned";
}

function isScheduledWithoutInstaller(job: Job): boolean {
  return Boolean(String(job.scheduledInstallDate ?? "").trim()) && !hasInstallerAssignment(job);
}

function isPastInstallDate(dateIso: string): boolean {
  const normalized = String(dateIso || "").trim().slice(0, 10);
  if (!normalized) return false;
  return normalized < formatInstallDateIso(new Date());
}

export function ReadyJobBookingDialog({
  open,
  onOpenChange,
  slotStart,
  onSlotStartChange,
  defaultDurationHours = 4,
  preselectJobId = null,
  scheduleReadyByJobId,
}: Props) {
  const { jobs, setJobs, issues, buildReviewEvents, contractors, opportunities, credits, materialKits } = useAppData();
  const onboarding = useOnboarding();
  const { events, crews, addEvent } = useScheduling();
  const withdrawnContractorIds = useMemo(
    () =>
      new Set(
        getOnboardingLifecycleRows(onboarding.inviteTokens, onboarding.sessions)
          .filter((row) => row.stage === "withdrawn" && row.contractorId)
          .map((row) => row.contractorId as string),
      ),
    [onboarding.inviteTokens, onboarding.sessions],
  );
  const schedulingContractors = useMemo(
    () => contractors.filter((contractor) => !withdrawnContractorIds.has(contractor.id)),
    [contractors, withdrawnContractorIds],
  );

  const [includeWarning, setIncludeWarning] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState<string>("all");

  // When opened from a specific card, preselect that job.
  // Do NOT force includeWarning — the preselected job is always shown
  // individually even if at-risk; other at-risk jobs stay hidden by default.
  useEffect(() => {
    if (open && preselectJobId) {
      setSelectedJobId(preselectJobId);
    }
    if (!open) {
      setSelectedJobId(null);
    }
  }, [open, preselectJobId]);
  const [selectedCrewId, setSelectedCrewId] = useState<string>("");
  const [durationValue, setDurationValue] = useState<string>(() => getDefaultDurationValue(defaultDurationHours));
  const selectedDuration = useMemo(() => getDurationOption(durationValue), [durationValue]);

  useEffect(() => {
    if (open) {
      setDurationValue(getDefaultDurationValue(defaultDurationHours));
    }
  }, [open, defaultDurationHours]);

  // Bookable candidates ranked by readiness, then by stage.
  const candidates = useMemo(() => {
    const list = jobs
      // Unscheduled jobs are normal candidates. Scheduled-but-unassigned jobs
      // are also candidates because this modal is how ops attaches a crew to
      // an orphaned schedule without hunting for it elsewhere.
      .filter((j) => !j.scheduledInstallDate || isScheduledWithoutInstaller(j))
      .map(j => {
        const openIssues = issues.filter(
          i => i.jobId === j.id && (i.status === "Open" || i.status === "In Progress"),
        );
        const kit = getKit(materialKits, j.id);
        const readiness = getCanonicalInstallReadiness(j, openIssues, buildReviewEvents, kit);
        const effectiveFailedChecks = readiness.checks.filter(
          (check) => !check.ok && !BOOKING_IGNORE_LABELS.has(check.label),
        );
        const preSpendGate = derivePreSpendGate(j);
        const dispatch = getDispatchRecommendations(
          j,
          schedulingContractors,
          opportunities,
          jobs,
          issues,
          credits,
          buildReviewEvents,
          materialKits,
        );
        const fromPlanGate = scheduleReadyByJobId?.get(j.id);
        const scheduleReady = fromPlanGate
          ? fromPlanGate.scheduleReady
          : (preSpendGate.cleared && dispatch.gateBlockers.length === 0);
        // Use the same gate as the "Ready to book" rail in capacityEngine.ts:
        // scheduleReady is the sole criterion — matches exactly what puts a job
        // in the green "ready" section of the rail.
        const bookingStatus = scheduleReady ? "Ready for Install" : "Not Ready";
        // const scheduleBlockers = [
        //   ...(preSpendGate.cleared ? [] : [preSpendGate.shortReason || "Pre-spend gate not cleared"]),
        //   ...dispatch.gateBlockers.map((g) => g.label),
        // ].filter((s): s is string => !!s && s.trim().length > 0);
        const scheduleBlockers = fromPlanGate
          ? [fromPlanGate.blocker].filter((s): s is string => !!s && s.trim().length > 0)
          : [
              ...(preSpendGate.cleared ? [] : [preSpendGate.shortReason || "Pre-spend gate not cleared"]),
              ...dispatch.gateBlockers.map((g) => g.label),
            ].filter((s): s is string => !!s && s.trim().length > 0);
        const effectiveGapCount = scheduleReady
          ? effectiveFailedChecks.length
          : Math.max(effectiveFailedChecks.length, scheduleBlockers.length, 1);
        return {
          job: j,
          readiness,
          bookingStatus,
          effectiveGapCount,
          scheduledWithoutInstaller: isScheduledWithoutInstaller(j),
        };
      })
      .filter(({ bookingStatus, job }) =>
        bookingStatus === "Ready for Install" ||
        job.id === preselectJobId ||
        (includeWarning && bookingStatus === "Not Ready"),
      );

    list.sort((a, b) => {
      // Pinned preselection wins
      if (preselectJobId) {
        if (a.job.id === preselectJobId) return -1;
        if (b.job.id === preselectJobId) return 1;
      }
      // Ready first
      if (a.bookingStatus !== b.bookingStatus) {
        return a.bookingStatus === "Ready for Install" ? -1 : 1;
      }
      // Then by score desc
      return b.readiness.score - a.readiness.score;
    });
    return list;
  }, [
    jobs,
    issues,
    buildReviewEvents,
    schedulingContractors,
    opportunities,
    credits,
    // includeWarning,
    // preselectJobId,
    materialKits,
    includeWarning,
    preselectJobId,
    scheduleReadyByJobId,
  ]);

  const projectOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of candidates) {
      const p = String(c.job.projectType ?? "").trim();
      if (p) set.add(p);
    }
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [candidates]);

  const filteredCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter(({ job }) => {
      const projectOk = projectFilter === "all" || String(job.projectType ?? "") === projectFilter;
      if (!projectOk) return false;
      if (!q) return true;
      const haystack = [
        getJobDisplayRef(job),
        job.customerName,
        job.projectType,
        job.address?.city,
        job.address?.state,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [candidates, search, projectFilter]);

  const activeSubcontractorNames = useMemo(
    () => new Set(schedulingContractors.map((contractor) => contractor.companyName)),
    [schedulingContractors],
  );
  const activeCrews = useMemo(
    () =>
      crews.filter((crew) => {
        if (!crew.active) return false;
        if (crew.type !== "subcontractor") return true;
        return activeSubcontractorNames.has(crew.name);
      }),
    [crews, activeSubcontractorNames],
  );
  const bookingWindow = useMemo(() => {
    if (!slotStart) return null;
    const start = new Date(slotStart);
    return {
      start,
      end: getBookingEnd(start, selectedDuration),
    };
  }, [slotStart, selectedDuration]);

  const crewConflicts = useMemo(() => {
    const conflictsByCrew = new Map<string, CrewConflictInfo>();
    if (!bookingWindow) return conflictsByCrew;

    const start = bookingWindow.start.getTime();
    const end = bookingWindow.end.getTime();

    for (const event of events) {
      if (event.status === "cancelled" || event.status === "completed") continue;
      const eventStart = parseISO(event.startTime).getTime();
      const eventEnd = parseISO(event.endTime).getTime();
      if (start < eventEnd && eventStart < end && !conflictsByCrew.has(event.crewId)) {
        conflictsByCrew.set(event.crewId, {
          crewId: event.crewId,
          title: event.title,
          startTime: event.startTime,
          endTime: event.endTime,
        });
      }
    }

    return conflictsByCrew;
  }, [bookingWindow, events]);

  const installDateSuggestions = useMemo<InstallDateSuggestion[]>(() => {
    if (activeCrews.length === 0) return [];

    const todayIso = formatInstallDateIso(new Date());
    const selectedIso = slotStart ? formatInstallDateIso(slotStart) : todayIso;
    let cursorIso = getNextInstallBusinessDateIso(selectedIso < todayIso ? todayIso : selectedIso);
    const suggestions: InstallDateSuggestion[] = [];
    const now = Date.now();
    let scannedDays = 0;

    while (suggestions.length < 5 && scannedDays < 21) {
      const start = createInstallDateTime(cursorIso, 9);
      const end = getBookingEnd(start, selectedDuration);
      const clearCrew = start.getTime() > now
        ? activeCrews.find((crew) => !overlapsEvent(start, end, crew.id, events))
        : null;

      if (clearCrew) {
        suggestions.push({
          id: `${cursorIso}-${clearCrew.id}`,
          crewId: clearCrew.id,
          crewName: clearCrew.name,
          start,
          end,
        });
      }

      cursorIso = formatInstallDateIso(advanceInstallDay(start));
      scannedDays += 1;
    }

    return suggestions;
  }, [activeCrews, events, selectedDuration, slotStart]);

  // Reset selection when filters remove the currently selected job.
  useEffect(() => {
    if (selectedJobId && !filteredCandidates.some((c) => c.job.id === selectedJobId)) {
      setSelectedJobId(null);
    }
  }, [selectedJobId, filteredCandidates]);

  // QoL: when search/filter narrows to exactly one row, auto-select it.
  useEffect(() => {
    if (selectedJobId) return;
    if (filteredCandidates.length !== 1) return;
    setSelectedJobId(filteredCandidates[0].job.id);
  }, [selectedJobId, filteredCandidates]);

  // Default crew when dialog opens or crews load. Prefer a crew with no overlap
  // for the chosen slot, so the first suggested path is conflict-free.
  useEffect(() => {
    if (selectedCrewId || activeCrews.length === 0) return;
    const firstClearCrew = activeCrews.find((crew) => !crewConflicts.has(crew.id));
    setSelectedCrewId((firstClearCrew ?? activeCrews[0]).id);
  }, [selectedCrewId, activeCrews, crewConflicts]);

  // Detect overlap on the chosen crew at the proposed window.
  const conflictWarning = useMemo(() => {
    if (!selectedCrewId) return null;
    const overlap = crewConflicts.get(selectedCrewId);
    if (!overlap) return null;
    return `Overlaps "${overlap.title}" on this crew.`;
  }, [selectedCrewId, crewConflicts]);

  const allCrewsConflictWarning = useMemo(() => {
    if (!slotStart || activeCrews.length === 0) return null;
    if (crewConflicts.size < activeCrews.length) return null;
    return "Every active crew has an overlap for this schedule window. You can still book, but it will be flagged as a conflict.";
  }, [slotStart, activeCrews, crewConflicts]);

  const handleApplySuggestion = (suggestion: InstallDateSuggestion) => {
    setSelectedCrewId(suggestion.crewId);
    onSlotStartChange?.(suggestion.start);
  };

  const selected = filteredCandidates.find(c => c.job.id === selectedJobId) ?? null;
  const slotDateIso = slotStart ? formatInstallDateIso(slotStart) : "";
  const slotIsPast = !!slotDateIso && isPastInstallDate(slotDateIso);
  const UNASSIGNED_CREW_VALUE = "__unassigned__";
  const isUnassignedSelected = selectedCrewId === UNASSIGNED_CREW_VALUE;
  const canBook = !!selected && (!!selectedCrewId) && !!slotStart;
  const bookingDisabledReason = !selected
    ? "Select a job to continue."
    : !slotStart
      ? "Pick a calendar slot first."
      : !selectedCrewId
        ? "Select a crew or choose Unassigned."
        : slotIsPast
          ? "This schedule is in the past. You'll be asked to confirm before booking."
          : null;

 const handleBook = async () => {
    if (!selected || !slotStart) return;

    const start = new Date(slotStart);
    const end = getBookingEnd(start, selectedDuration);
    const startIso = start.toISOString();
    const endIso = end.toISOString();
    const scheduledShifts = getBookingShiftCount(selectedDuration);
    const now = new Date().toISOString();

    // ── Unassigned path: book date without a crew ────────────────────
    if (isUnassignedSelected) {
      const scheduleDateIso = formatInstallDateIso(start);
      if (isPastInstallDate(scheduleDateIso)) {
        const confirmed = window.confirm(
          `This will schedule ${getJobDisplayRef(selected.job)} to a past install date (${scheduleDateIso}). Continue?`,
        );
        if (!confirmed) return;
      }
      const optimisticJob: Job = {
        ...selected.job,
        scheduledInstallDate: scheduleDateIso,
        scheduledStartTime: startIso,
        scheduledEndTime: endIso,
        scheduledShifts,
        timezone: INSTALL_TIME_ZONE,
        assignmentType: undefined,
        crewId: undefined,
        assignedCrew: "",
        assignedSubcontractor: "",
        schedulingStatus: "Scheduled" as Job["schedulingStatus"],
      };
      setJobs(prev => prev.map(j => (j.id === selected.job.id ? optimisticJob : j)));
      try {
        const { data, error } = await JobService.update(selected.job.id, {
          scheduledInstallDate: scheduleDateIso,
          scheduledStartTime: startIso,
          scheduledEndTime: endIso,
          scheduledShifts,
          timezone: INSTALL_TIME_ZONE,
          assignmentType: undefined,
          crewId: "",
          assignedCrew: "",
          assignedSubcontractor: "",
          schedulingStatus: "Scheduled",
        });
        if (error || !data) throw error ?? new Error("Failed to persist booking");
        setJobs(prev => prev.map(j => (j.id === data.id ? data : j)));
        toast({ title: "Scheduled · no crew assigned", description: `Date set to ${scheduleDateIso}` });
        setSelectedJobId(null);
        onOpenChange(false);
      } catch (err) {
        console.error("[ReadyJobBookingDialog] Unassigned persist failed:", err);
        setJobs(prev => prev.map(j => (j.id === selected.job.id ? selected.job : j)));
        toast({ title: "Schedule failed", description: "Changes were reverted.", variant: "destructive" });
      }
      return;
    }

    const crew = activeCrews.find(c => c.id === selectedCrewId);
    if (!crew) return;

    const assignmentType: Job["assignmentType"] =
      crew.type === "subcontractor" ? "Subcontractor" : "Internal Crew";

    const assignedCrew = assignmentType === "Internal Crew" ? crew.name : "";
    const assignedSubcontractor = assignmentType === "Subcontractor" ? crew.name : "";
    const crewId = assignmentType === "Internal Crew" ? crew.id : undefined;

    const scheduleDateIso = formatInstallDateIso(start);

    if (isPastInstallDate(scheduleDateIso)) {
      const confirmed = window.confirm(
        `This will schedule ${getJobDisplayRef(selected.job)} to a past install date (${scheduleDateIso}). Continue?`,
      );
      if (!confirmed) return;
    }

    const nextDispatchStatus: Job["dispatchStatus"] =
      selected.job.dispatchStatus === "Assigned" ||
      selected.job.dispatchStatus === "Accepted" ||
      selected.job.dispatchStatus === "Rejected" ||
      selected.job.dispatchStatus === "Closed"
        ? selected.job.dispatchStatus
        : "Assigned";

    const previousJob = selected.job;

    const optimisticJob: Job = {
      ...selected.job,
      scheduledInstallDate: scheduleDateIso,
      scheduledStartTime: startIso,
      scheduledEndTime: endIso,
      scheduledShifts,
      timezone: INSTALL_TIME_ZONE,
      assignmentType,
      crewId,
      assignedCrew,
      assignedSubcontractor,
      dispatchStatus: nextDispatchStatus,
      schedulingStatus: "Scheduled" as Job["schedulingStatus"],
    };

    // ✅ optimistic update
    setJobs(prev =>
      prev.map(j => (j.id === selected.job.id ? optimisticJob : j))
    );

    try {
      const { data, error } = await JobService.update(selected.job.id, {
        scheduledInstallDate: scheduleDateIso,
        scheduledStartTime: startIso,
        scheduledEndTime: endIso,
        scheduledShifts,
        timezone: INSTALL_TIME_ZONE,
        assignmentType,
        crewId,
        assignedCrew,
        assignedSubcontractor,
        dispatchStatus: nextDispatchStatus,
        schedulingStatus: "Scheduled",
      });

      if (error || !data) {
        throw error ?? new Error("Failed to persist booking");
      }

      // ✅ sync with DB
      setJobs(prev =>
        prev.map(j => (j.id === data.id ? data : j))
      );

      await JobOperationsService.createSchedulingEvent({
        jobNumber: selected.job.id,
        contractorId: assignmentType === "Subcontractor" ? (crew.id ?? null) : null,
        eventType: "schedule_set",
        startAt: startIso,
        endAt: endIso,
        status: "scheduled",
        notes: JSON.stringify({
          source: "ready_job_booking_dialog",
          previous: {
            scheduledInstallDate: previousJob.scheduledInstallDate,
            scheduledStartTime: previousJob.scheduledStartTime,
            scheduledEndTime: previousJob.scheduledEndTime,
            scheduledShifts: previousJob.scheduledShifts,
            assignmentType: previousJob.assignmentType,
            assignedCrew: previousJob.assignedCrew,
            assignedSubcontractor: previousJob.assignedSubcontractor,
          },
          next: {
            scheduledInstallDate: scheduleDateIso,
            scheduledStartTime: startIso,
            scheduledEndTime: endIso,
            scheduledShifts,
            timezone: INSTALL_TIME_ZONE,
            assignmentType,
            assignedCrew,
            assignedSubcontractor,
            dispatchStatus: nextDispatchStatus,
          },
        }),
        briefSnapshot: getJobBriefing(optimisticJob, getPortalSpec(optimisticJob), await getJobPhotoUrls(optimisticJob.id)) as unknown as Record<string, unknown>,
      });

      toast({
        title: conflictWarning ? "Booked — conflict detected" : "Install booked",
        description: `${selected.job.projectType} -> ${formatInstallDateTimeLabel(start)} for ${selectedDuration.label}`,
        variant: conflictWarning ? "destructive" : "default",
      });

      setSelectedJobId(null);
      onOpenChange(false);

    } catch (err) {
      console.error("[ReadyJobBookingDialog] Persist failed:", err);

      // 🔥 rollback
      setJobs(prev =>
        prev.map(j => (j.id === previousJob.id ? previousJob : j))
      );

      toast({
        title: "Schedule failed",
        description: "Changes were reverted due to save failure.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-primary" />
            Book an install
          </DialogTitle>
          <DialogDescription>
            {slotStart
              ? `${formatInstallDateTimeLabel(slotStart)} - pick a ready job and a crew.`
              : "Select a slot on the calendar."}
          </DialogDescription>
        </DialogHeader>

        {/* Filter toggle */}
        <div className="flex items-center justify-between border-b border-border pb-3">
          <p className="text-xs text-muted-foreground">
            Showing {filteredCandidates.length} of {candidates.length} bookable job{candidates.length === 1 ? "" : "s"}
            {!includeWarning && " (ready only)"}
          </p>
          <button
            type="button"
            onClick={() => setIncludeWarning(prev => !prev)}
            className="text-xs text-primary hover:underline"
          >
            {includeWarning ? "Hide at-risk jobs" : "Include at-risk jobs"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-2 pt-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by job #, customer, project, city"
            className="h-9"
          />
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="All project types" />
            </SelectTrigger>
            <SelectContent>
              {projectOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option === "all" ? "All project types" : option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Candidate list */}
        <div className="max-h-[320px] overflow-y-auto -mx-1 px-1 space-y-1.5">
          {filteredCandidates.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {candidates.length === 0 ? "No bookable jobs right now." : "No jobs match your search/filter."}
              {candidates.length === 0 && !includeWarning && (
                <button
                  type="button"
                  onClick={() => setIncludeWarning(true)}
                  className="ml-1 text-primary hover:underline"
                >
                  Include at-risk?
                </button>
              )}
            </div>
          )}
          {filteredCandidates.map(({ job, readiness, bookingStatus, effectiveGapCount, scheduledWithoutInstaller }) => {
            const active = selectedJobId === job.id;
            const isReady = bookingStatus === "Ready for Install";
            return (
              <button
                key={job.id}
                type="button"
                onClick={() => setSelectedJobId(job.id)}
                className={`w-full text-left rounded-md border p-3 transition-colors ${
                  active
                    ? "border-primary/50 bg-primary/5 ring-1 ring-inset ring-primary/30"
                    : "border-border hover:bg-muted/40"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {isReady ? (
                        <ShieldCheck className="h-3.5 w-3.5 text-success shrink-0" />
                      ) : (
                        <ShieldAlert className="h-3.5 w-3.5 text-warning shrink-0" />
                      )}
                      <span className="text-sm font-semibold text-foreground truncate">
                        {getJobDisplayRef(job)} · {job.customerName}
                      </span>
                      <Badge
                        variant="outline"
                        className={
                          isReady
                            ? "text-[10px] bg-success/10 text-success border-success/20"
                            : "text-[10px] bg-warning/10 text-warning border-warning/20"
                        }
                      >
                        {isReady ? "Ready" : "At risk"}
                      </Badge>
                      {scheduledWithoutInstaller && (
                        <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/20">
                          Scheduled · needs crew
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {job.projectType} · {job.address.city}, {job.address.state}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-medium text-foreground">{readiness.score}%</p>
                    <p className="text-[10px] text-muted-foreground">
                      {effectiveGapCount === 0
                        ? "all clear"
                        : `${effectiveGapCount} gap${effectiveGapCount === 1 ? "" : "s"}`}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Booking controls */}
        {selected && (
          <div className="border-t border-border pt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                Crew
              </label>
              <Select value={selectedCrewId} onValueChange={setSelectedCrewId}>
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue placeholder="Choose crew" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED_CREW_VALUE}>
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <span className="inline-block h-2 w-2 rounded-full border border-muted-foreground/40" />
                      Unassigned
                    </span>
                  </SelectItem>
                  {activeCrews.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: c.color }}
                        />
                        {c.name} <span className="text-muted-foreground">· {c.type}</span>
                        {crewConflicts.has(c.id) && (
                          <span className="ml-auto text-warning">conflict</span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                Duration / span
              </label>
              <Select
                value={durationValue}
                onValueChange={setDurationValue}
              >
                <SelectTrigger className="h-9 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedDuration.days && selectedDuration.days > 1 && (
                <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                  Reserves through 5:00 PM on the final day.
                </p>
              )}
            </div>

            {conflictWarning && (
              <div className="col-span-2 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2">
                <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
                <p className="text-xs text-warning">{conflictWarning} You can still book — the conflict will be flagged.</p>
              </div>
            )}
            {!conflictWarning && allCrewsConflictWarning && (
              <div className="col-span-2 flex items-start gap-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2">
                <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
                <p className="text-xs text-warning">{allCrewsConflictWarning}</p>
              </div>
            )}
            {installDateSuggestions.length > 0 && (
              <div className="col-span-2 rounded-md border border-border bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-foreground">Suggested dates</p>
                    <p className="text-[11px] text-muted-foreground">
                      Conflict-free options, never before today in New York.
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {INSTALL_TIME_ZONE}
                  </Badge>
                </div>
                <div className="mt-2 grid gap-2">
                  {installDateSuggestions.map((suggestion, index) => (
                    <Button
                      key={suggestion.id}
                      type="button"
                      variant="outline"
                      className="h-auto justify-start px-3 py-2 text-left"
                      onClick={() => handleApplySuggestion(suggestion)}
                    >
                      <span className="flex w-full items-center justify-between gap-3">
                        <span className="min-w-0">
                          <span className="block text-xs font-medium">
                            {index === 0 ? "Best: " : ""}
                            {formatSuggestionRange(suggestion, selectedDuration)}
                          </span>
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {suggestion.crewName} · {selectedDuration.label} · no overlap across span
                          </span>
                        </span>
                        <span className="shrink-0 text-[11px] text-success">clear</span>
                      </span>
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {installDateSuggestions.length === 0 && activeCrews.length > 0 && (
              <div className="col-span-2 rounded-md border border-warning/30 bg-warning/5 px-3 py-2">
                <p className="text-xs text-warning">
                  No conflict-free suggestions found in the next 21 install days. Manual booking is still available and will warn on overlap.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {bookingDisabledReason && (
            <p className="mr-auto text-xs text-muted-foreground">{bookingDisabledReason}</p>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleBook} disabled={!canBook}>
            Book install
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
